if (!customElements.get('product-slider-thumbnails')) {
  /**
   *  @class
   *  @function ProductSliderThumbnails
   */
  class ProductSliderThumbnails extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', this.setupZoomListeners);
      // Rubin (2026-10-18, GALERIE LADEN FIX): load-state guards for slide images.
      this.addEventListener('lazyloaded', (event) => this.rxVerifyLoaded(event.target));
      this.addEventListener('error', (event) => this.rxHandleError(event.target), true);
    }
    connectedCallback() {
      this.product_container = this.closest('.thb-product-detail');
      this.thumbnail_container = this.product_container.querySelector('.product-thumbnail-container');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');

      this.setOptions();
      this.init();
    }
    setOptions() {
      this.thumbnails = this.thumbnail_container.querySelectorAll('.product-thumbnail');
      this.prev_button = this.querySelector('.flickity-prev');
      this.next_button = this.querySelector('.flickity-next');
      this.options = {
        wrapAround: true,
        pageDots: false,
        contain: true,
        adaptiveHeight: true,
        initialIndex: '.is-initial-selected',
        prevNextButtons: false,
        fade: false,
        cellSelector: '.product-images__slide.is-active'
      };
    }
    init() {
      this.flkty = new Flickity(this, this.options);
      this.selectedIndex = this.flkty.selectedIndex;
      this.setupEvents();
      this.setupZoomListeners();
      // Flickity fires no change event for the initial cell, so the active
      // thumbnail would otherwise start out of view on a long list. Position
      // it instantly rather than animating a scroll on page load.
      this.scrollToActiveThumbnail('auto');
      // Rubin (2026-10-18, GALERIE LADEN FIX): the first slide keeps its own
      // eager/high-priority image; its neighbours wait until the page has loaded.
      this.rxLastIndex = this.flkty.selectedIndex;
      this.rxCellImages(this.rxLastIndex).forEach((img) => this.rxUnveil(img, 'high'));
      this.rxScheduleIdleNeighbours();
    }
    reInit() {
      if (this.eventController) {
        this.eventController.abort();
      }

      this.flkty.destroy();
      this.setOptions();
      this.flkty = new Flickity(this, this.options);
      this.setupEvents();
      this.selectedIndex = this.flkty.selectedIndex;
      // A variant change marks the new cell as initial before rebuilding, so
      // the follow-up selectCell lands on it without firing change — scroll
      // the matching thumbnail into view explicitly.
      this.scrollToActiveThumbnail();
      // Rubin (2026-10-18, GALERIE LADEN FIX): Flickity emits no select event
      // while it is being rebuilt, so load the new start slide explicitly.
      this.rxReleaseAll();
      this.rxLastIndex = this.flkty.selectedIndex;
      this.rxLoadSlide(this.rxLastIndex);
    }
    setupEvents() {
      if (this.eventController) {
        this.eventController.abort();
      }
      this.eventController = new AbortController();
      const signal = this.eventController.signal;

      if (this.prev_button) {
        this.prev_button.addEventListener('click', () => {
          this.flkty.previous();
        }, { signal });
        this.prev_button.addEventListener('keyup', (event) => {
          this.flkty.previous();
          event.preventDefault();
        }, { signal });
      }
      if (this.next_button) {
        this.next_button.addEventListener('click', () => {
          this.flkty.next();
        }, { signal });
        this.next_button.addEventListener('keyup', (event) => {
          this.flkty.next();
          event.preventDefault();
        }, { signal });
      }
      this.video_containers.forEach((container) => {
        container.querySelector('button').addEventListener('click', function () {
          container.setAttribute('hidden', '');
        });
      });
      this.flkty.on('settle', (index) => {
        this.selectedIndex = index;
        this.rxReleaseAll();
      });
      // Rubin (2026-10-18, GALERIE LADEN FIX): every way of choosing a slide
      // (thumbnail, swipe release, arrows, keyboard, zoom, variant change) ends
      // in Flickity's select event, which now starts the download right away.
      this.flkty.on('select', () => {
        this.rxOnSelect();
      });
      this.flkty.on('dragStart', () => {
        this.rxDragPrimed = false;
        this.rxReleaseAll();
      });
      this.flkty.on('dragMove', (event, pointer, moveVector) => {
        if (this.rxDragPrimed || !moveVector || Math.abs(moveVector.x) < 10) {
          return;
        }
        this.rxDragPrimed = true;
        const next = this.flkty.selectedIndex + (moveVector.x < 0 ? 1 : -1);
        this.rxCellImages(next).forEach((img) => this.rxUnveil(img, 'high'));
      });
      this.flkty.on('change', (index) => {
        let previous_slide = this.flkty.cells[this.selectedIndex].element,
          previous_media = previous_slide.querySelector('.product-single__media'),
          current_slide = this.flkty.cells[index].element,
          thumbId = current_slide.id.replace('Slide-', 'Thumb-'),
          active_thumb = this.thumbnail_container.querySelector('#' + thumbId);

        this.thumbnails.forEach((item) => {
          item.classList.remove('is-initial-selected');
        });
        if (active_thumb) {
          active_thumb.classList.add('is-initial-selected');
          this.scrollToThumbnail(active_thumb);
        }
        this.pauseMedia(previous_media);
      });

      setTimeout(() => {
        let active_thumbs = Array.from(this.thumbnails).filter(element => element.clientWidth > 0);
        active_thumbs.forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => {
            this.thumbnailClick(thumbnail);
          }, { signal });
        });
      });
    }
    scrollToActiveThumbnail(behavior = 'smooth') {
      if (!this.flkty || !this.flkty.cells.length) {
        return;
      }
      const current_slide = this.flkty.cells[this.flkty.selectedIndex].element,
        thumbId = current_slide.id.replace('Slide-', 'Thumb-'),
        active_thumb = this.thumbnail_container.querySelector('#' + thumbId);
      if (active_thumb) {
        this.scrollToThumbnail(active_thumb, behavior);
      }
    }
    scrollToThumbnail(thumb, behavior = 'smooth') {
      requestAnimationFrame(() => {
        if (thumb.offsetParent === null) {
          return;
        }
        const windowHalfHeight = thumb.offsetParent.clientHeight / 2,
          windowHalfWidth = thumb.offsetParent.clientWidth / 2;
        thumb.parentElement.scrollTo({
          left: thumb.offsetLeft - windowHalfWidth + thumb.clientWidth / 2,
          top: thumb.offsetTop - windowHalfHeight + thumb.clientHeight / 2,
          behavior: behavior
        });
      });
    }
    pauseMedia(media) {
      if (media.classList.contains('product-single__media-external-video')) {
        if (media.dataset.provider === 'youtube') {
          media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
            event: "command",
            func: "pauseVideo",
            args: ""
          }), "*");
        } else if (media.dataset.provider === 'vimeo') {
          media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
            method: "pause"
          }), "*");
        }
        media.querySelector('.product-single__media-external-video--play').removeAttribute('hidden');
      } else if (media.classList.contains('product-single__media-native-video')) {
        media.querySelector('video').pause();
      }
    }
    thumbnailClick(thumbnail) {
      this.thumbnails.forEach((el) => {
        el.classList.remove('is-initial-selected');
      });
      thumbnail.classList.add('is-initial-selected');
      let mediaId = '#' + thumbnail.id.replace('Thumb-', 'Slide-');
      this.flkty.selectCell(mediaId);
    }
    /* Rubin (2026-10-18, GALERIE LADEN FIX) ----------------------------------
       lazySizes stays the only loader for slide images. Instead of waiting for
       its throttled visibility check (which also pauses while 8+ images are in
       flight and skips images whose 20 px placeholder is still downloading),
       the gallery tells it which slide to load:
       - the selected slide is unveiled immediately with fetchpriority="high";
       - its two neighbours follow with fetchpriority="low" once it has loaded
         (on page load: after window.load, when the browser is idle);
       - on a jump over several slides, the slides only passed on the way are
         held back (lazySizes data-expand) until the slider settles, so they do
         not compete with the selected image for bandwidth;
       - an image counts as loaded only when its real file is shown, never the
         20 px placeholder; a failed download is retried once, after that the
         blur is removed so the gallery never stays blurred. */
    rxCellIndex(index) {
      const length = this.flkty ? this.flkty.cells.length : 0;
      return length ? ((index % length) + length) % length : -1;
    }
    rxCellImages(index) {
      const cellIndex = this.rxCellIndex(index);
      if (cellIndex < 0) {
        return [];
      }
      return Array.from(this.flkty.cells[cellIndex].element.querySelectorAll('.product-single__media-image img'));
    }
    rxIsPlaceholder(img) {
      return /[?&]width=20(&|$)/.test(img.currentSrc || img.getAttribute('src') || '');
    }
    rxIsReady(img) {
      return img.complete && img.naturalWidth > 0 && !!img.currentSrc && !this.rxIsPlaceholder(img);
    }
    rxUnveil(img, priority) {
      if (!img) {
        return;
      }
      if (img.dataset.rxHold) {
        this.rxRelease(img);
      }
      if (!img.classList.contains('lazyload') || !window.lazySizes) {
        return;
      }
      if (priority === 'high' || img.getAttribute('fetchpriority') !== 'high') {
        img.setAttribute('fetchpriority', priority);
      }
      // lazySizes refuses to unveil a data-sizes="auto" image while its 20 px
      // placeholder is still downloading. Once the real file is requested the
      // placeholder is useless, so drop it instead of waiting for it.
      if (!img.complete && /[?&]width=20(&|$)/.test(img.getAttribute('src') || '')) {
        img.removeAttribute('src');
      }
      window.lazySizes.loader.unveil(img);
    }
    rxLoadSlide(index) {
      const target = this.rxCellIndex(index);
      if (target < 0) {
        return;
      }
      const images = this.rxCellImages(target);
      images.forEach((img) => this.rxUnveil(img, 'high'));
      clearTimeout(this.rxNeighbourTimer);
      const pending = images.filter((img) => !this.rxIsReady(img));
      let started = false;
      const loadNeighbours = () => {
        if (started) {
          return;
        }
        started = true;
        clearTimeout(this.rxNeighbourTimer);
        if (!this.flkty || this.flkty.selectedIndex !== target) {
          return;
        }
        this.rxLoadNeighbours(target);
      };
      if (!pending.length) {
        loadNeighbours();
        return;
      }
      pending.forEach((img) => {
        img.addEventListener('load', loadNeighbours, { once: true });
        img.addEventListener('error', loadNeighbours, { once: true });
      });
      this.rxNeighbourTimer = setTimeout(loadNeighbours, 3000);
    }
    rxLoadNeighbours(index) {
      [index - 1, index + 1].forEach((neighbour) => {
        this.rxCellImages(neighbour).forEach((img) => this.rxUnveil(img, 'low'));
      });
    }
    rxOnSelect() {
      const target = this.flkty.selectedIndex,
        previous = typeof this.rxLastIndex === 'number' ? this.rxLastIndex : target,
        length = this.flkty.cells.length,
        step = this.rxCellIndex(target - previous),
        distance = Math.min(step, length - step);
      this.rxLastIndex = target;
      if (distance > 1) {
        this.rxHoldPassedSlides(target);
      } else {
        this.rxReleaseAll();
      }
      this.rxLoadSlide(target);
    }
    rxHoldPassedSlides(target) {
      this.rxReleaseAll();
      const keep = [target - 1, target, target + 1].map((index) => this.rxCellIndex(index));
      this.flkty.cells.forEach((cell, index) => {
        if (keep.includes(index)) {
          return;
        }
        cell.element.querySelectorAll('img.lazyload').forEach((img) => {
          if (img.hasAttribute('data-expand')) {
            return;
          }
          img.setAttribute('data-expand', '-9999');
          img.dataset.rxHold = '1';
        });
      });
      this.rxHoldTimer = setTimeout(() => this.rxReleaseAll(), 2500);
    }
    rxRelease(img) {
      img.removeAttribute('data-expand');
      delete img.dataset.rxHold;
    }
    rxReleaseAll() {
      clearTimeout(this.rxHoldTimer);
      this.querySelectorAll('img[data-rx-hold]').forEach((img) => this.rxRelease(img));
    }
    rxScheduleIdleNeighbours() {
      const run = () => {
        const whenIdle = window.requestIdleCallback
          ? (callback) => window.requestIdleCallback(callback, { timeout: 2000 })
          : (callback) => setTimeout(callback, 800);
        whenIdle(() => {
          if (this.flkty) {
            this.rxLoadNeighbours(this.flkty.selectedIndex);
          }
        });
      };
      if (document.readyState === 'complete') {
        run();
      } else {
        window.addEventListener('load', run, { once: true });
      }
    }
    rxVerifyLoaded(img) {
      if (!img || img.tagName !== 'IMG' || !img.closest('.product-single__media-image')) {
        return;
      }
      if (this.rxIsReady(img)) {
        img.classList.remove('rx-img-pending');
        return;
      }
      // lazySizes switched to "loaded" while the browser still shows the
      // placeholder (or nothing): keep the loading look until the real file is in.
      img.classList.add('rx-img-pending');
      if (img.rxWaiting) {
        return;
      }
      img.rxWaiting = true;
      const onLoad = () => {
        if (!this.rxIsReady(img)) {
          return;
        }
        img.rxWaiting = false;
        img.removeEventListener('load', onLoad);
        img.classList.remove('rx-img-pending');
      };
      img.addEventListener('load', onLoad);
    }
    rxHandleError(img) {
      if (!img || img.tagName !== 'IMG' || !img.closest('.product-single__media-image') || !img.getAttribute('srcset')) {
        return;
      }
      const tries = Number(img.dataset.rxRetry || 0);
      if (tries < 1) {
        img.dataset.rxRetry = String(tries + 1);
        img.classList.add('rx-img-pending');
        setTimeout(() => {
          const srcset = img.getAttribute('srcset');
          if (srcset) {
            img.removeAttribute('srcset');
            img.setAttribute('srcset', srcset);
          }
        }, 1000);
        return;
      }
      img.rxWaiting = false;
      img.classList.remove('rx-img-pending', 'lazyload', 'lazyloading');
      img.classList.add('lazyloaded', 'lazyerror');
    }
    setDraggable(draggable) {
      this.flkty.options.draggable = draggable;
      this.flkty.updateDraggable();
    }
    selectCell(mediaId) {
      this.flkty.selectCell(mediaId);
    }
    setupZoomListeners() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }
      this.setEventListeners();

      document.addEventListener('product:variant-change', (e) => {
        if (e.detail && e.detail.variantTitle && e.detail.variantTitle !== 'Default Title') {
          this.dataset.variantTitle = e.detail.variantTitle;
          this.updatePswpProductInfo();
        }
      });
    }
    buildItems() {
      this.activeImages = Array.from(this.querySelectorAll('.product-images__slide.is-active .product-single__media-image'));

      return this.activeImages.map((item, index) => {
        let activelink = item.querySelector('.product-single__media-zoom'),
          slideImage = item.querySelector('img');

        activelink.dataset.index = index;
        return {
          src: activelink.getAttribute('href'),
          // Rubin (2026-10-18, GALERIE LADEN FIX): open the zoom on the sharp
          // image already shown in the slide instead of the 20 px placeholder.
          msrc: slideImage && this.rxIsReady(slideImage) ? slideImage.currentSrc : activelink.dataset.msrc,
          thumbSrc: activelink.dataset.thumbSrc || activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h
        };
      });
    }
    setEventListeners() {
      this.links = this.querySelectorAll('.product-single__media-zoom');
      this.pswpElement = document.querySelectorAll('.pswp')[0];
      this.pswpOptions = {
        maxSpreadZoom: 2,
        loop: true,
        allowPanToNext: true,
        closeOnScroll: false,
        showHideOpacity: false,
        arrowKeys: true,
        history: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: true,
        arrowEl: true,
        preloaderEl: true,
        getThumbBoundsFn: () => {
          const thumbnail = this.querySelector('.product-images__slide.is-selected'),
            pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();
          return {
            x: rect.left,
            y: rect.top + pageYScroll,
            w: rect.width
          };
        }
      };

      this.links.forEach((link => {
        link.addEventListener('click', (e) => this.zoomClick(e, link));
      }));
    }
    buildPswpThumbnails(items) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      container.innerHTML = '';
      if (this.dataset.lightboxThumbnails === 'false' || items.length <= 1) {
        container.parentElement.style.display = 'none';
        return;
      }
      container.parentElement.style.display = '';
      items.forEach((item, index) => {
        const thumb = document.createElement('div');
        thumb.className = 'pswp__thumb';
        thumb.dataset.index = index;
        const img = document.createElement('img');
        img.src = item.thumbSrc;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
        container.appendChild(thumb);
      });
    }
    updatePswpProductInfo() {
      const infoEl = this.pswpElement.querySelector('.pswp__product-info');
      if (!infoEl) return;
      if (this.dataset.lightboxProductInfo === 'false') {
        infoEl.style.display = 'none';
        return;
      }
      infoEl.style.display = '';
      const titleEl = infoEl.querySelector('.pswp__product-title');
      const variantEl = infoEl.querySelector('.pswp__variant-title');
      if (titleEl) titleEl.textContent = this.dataset.productTitle || '';
      if (variantEl) variantEl.textContent = this.dataset.variantTitle || '';
    }
    syncPswpThumbnails(index) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      const thumbs = container.querySelectorAll('.pswp__thumb');
      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
      });
      const activeThumb = container.querySelector('.pswp__thumb.is-active');
      if (activeThumb) {
        activeThumb.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
      }
    }
    zoomClick(e, link) {
      this.items = this.buildItems();
      this.pswpOptions.index = parseInt(link.dataset.index, 10);
      if (typeof PhotoSwipe !== 'undefined') {
        this.buildPswpThumbnails(this.items);
        this.updatePswpProductInfo();

        const bottomBar = this.pswpElement.querySelector('.pswp__bottom-bar');
        if (bottomBar) {
          ['mousedown', 'touchstart', 'pointerdown'].forEach(evtName => {
            bottomBar.addEventListener(evtName, (evt) => evt.stopPropagation());
          });
        }

        let pswp = new PhotoSwipe(this.pswpElement, PhotoSwipeUI_Default, this.items, this.pswpOptions);
        const _this = this;

        pswp.listen('firstUpdate', function () {
          pswp.listen('parseVerticalMargin', function (item) {
            item.vGap = {
              top: 50,
              bottom: 140
            };
          });
        });

        const scrollWrap = this.pswpElement.querySelector('.pswp__scroll-wrap');
        const resetScroll = function() {
          if (scrollWrap) scrollWrap.scrollLeft = 0;
          _this.pswpElement.scrollLeft = 0;
        };

        pswp.listen('beforeChange', function () {
          const index = pswp.getCurrentIndex();
          _this.syncPswpThumbnails(index);
          resetScroll();
          if (_this.flkty) {
            _this.flkty.select(index);
          }
        });

        const thumbsInner = this.pswpElement.querySelector('.pswp__thumbs-inner');
        if (thumbsInner) {
          thumbsInner.addEventListener('click', function handler(evt) {
            evt.stopPropagation();
            const thumb = evt.target.closest('.pswp__thumb');
            if (thumb) {
              pswp.goTo(parseInt(thumb.dataset.index, 10));
              resetScroll();
            }
          });
        }

        pswp.listen('close', function () {
          const container = _this.pswpElement.querySelector('.pswp__thumbs-inner');
          if (container) container.innerHTML = '';
        });

        pswp.init();
        this.syncPswpThumbnails(this.pswpOptions.index);
      }
      e.preventDefault();
    }
  }
  customElements.define('product-slider-thumbnails', ProductSliderThumbnails);
}
